import {createContext, ReactNode, useMemo, useState} from 'react'
import {GetCartQuery, SelectedShippingMethod, Showroom,} from '_app/graphql/generated/graphql'
import {CheckoutCartResponse} from '_app/graphql/types'
import {Maybe} from '_app/types/api'
import {INPOST_METHOD} from '../constants'
import {AddressDetails, STEPS} from '../types'
import {getSelectedShippingMethod} from '../utils/shippingMethod'
import {useReactiveVar} from '@apollo/client'
import {cartIdVar} from '../../../_app/cache'
import {useGetCartQueryGift} from '../../../_app/graphql/queries/cart/GetCartDocumentGift'

type CheckoutContextType = {
    step: STEPS;
    setStep: (step: STEPS) => void;
    cartCheckoutData: CheckoutCartResponse | undefined;
    cartSummaryData: GetCartQuery | undefined;
    inpostAddress: string;
    setInpostAddress: (address: string) => void;
    inpostAddressDetails: AddressDetails | undefined;
    setInpostAddressDetails: (address: AddressDetails) => void;
    setShippingMethodCode: (address: string) => void;
    isShowroomShippingSelected: boolean;
    isParcelLocker: boolean;
    selectedShowroom: Showroom | undefined;
    setSelectedShowroom: (showroom: Showroom | undefined) => void;
    shippingValue: number;
    setShippingValue: (value: number) => void;
    selectedShippingMethod: Maybe<SelectedShippingMethod> | undefined;
    areTermsAccepted: boolean;
    refreshCartData: () => void;
    setAreTermsAccepted: (value: boolean) => void;
};

export const CheckoutContext = createContext<CheckoutContextType | null>(null)

export const CheckoutContextProvider = ({
                                            children,
                                        }: {
    children: ReactNode;
}) => {
    const [step, setStep] = useState(STEPS.DELIVERY)
    const [selectedShowroom, setSelectedShowroom] = useState<Showroom>()
    const [inpostAddressDetails, setInpostAddressDetails] =
        useState<AddressDetails>()
    const [inpostAddress, setInpostAddress] = useState('')
    const [shippingMethodCode, setShippingMethodCode] = useState('')
    const [shippingValue, setShippingValue] = useState(0)
    const [areTermsAccepted, setAreTermsAccepted] = useState(false)
    const cartId: any = useReactiveVar(cartIdVar)
    const { data, refetch } = useGetCartQueryGift({
        variables: {
            cartId,
        },
        skip: !cartId,
    })
    const isShowroomShippingSelected = shippingMethodCode === 'showroom'
    const isParcelLocker = shippingMethodCode === INPOST_METHOD
    const selectedShippingMethod = data?.cart?.shipping_addresses !== undefined && data?.cart?.shipping_addresses?.length > 0 && getSelectedShippingMethod(data)

    const value: any = useMemo(
        () => ({
            step,
            setStep,
            cartCheckoutData: data,
            cartSummaryData: data,
            inpostAddress,
            setInpostAddress,
            inpostAddressDetails,
            setInpostAddressDetails,
            setShippingMethodCode,
            isShowroomShippingSelected,
            isParcelLocker,
            selectedShowroom,
            setSelectedShowroom,
            shippingValue,
            setShippingValue,
            selectedShippingMethod,
            areTermsAccepted,
            refreshCartData: () => refetch(),
            setAreTermsAccepted,
        }),
        [
            data,
            inpostAddress,
            inpostAddressDetails,
            isParcelLocker,
            isShowroomShippingSelected,
            selectedShowroom,
            shippingValue,
            step,
            selectedShippingMethod,
            areTermsAccepted,
        ]
    )
    return (
        <CheckoutContext.Provider value={value}>
            {children}
        </CheckoutContext.Provider>
    )
}
